import { buildMarketplaceUnits, getActiveMarketplaceIds, TMarketplaceUnit } from "@src/components/utils/marketplaceUtils";
import { MarketplaceIcon } from "@src/components/marketPlaces/MarketplaceIcon";
import { useAppSelector } from "@src/store/hooks";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { IGlobalInventoryRawItem } from "@src/store/pricing/queryGlobalInventorySlice";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "../Inventory.module.scss";
import { MyTooltip } from "@src/components/tooltip/Tooltip";
import { Loader } from "@src/components/loader/Loader";
import { SyncStatus, getSyncStatus, TSyncPrefix, TSyncStatus } from "@src/components/utils/syncStatus";
import { ExportDestination } from "@src/constants/constants";
import useGametimeListingStatusEnabled from "@src/hooks/useGametimeListingStatusEnabled";
import { GAMETIME_CURATION_DETAIL, showsGametimeCurationHint } from "@src/components/utils/gametimeListingStatus";

interface TMarketplaces {
	rowData: EventInventoryRecord;
}

const ICON_BOX = 18;
const ICON_GAP = 8;
const BADGE_WIDTH = 30;
const MERGE_GAP = 4;

type TSyncMarketplace = {
	prefix: TSyncPrefix;
	liveId: number;
	anchorId: number;
	renderOnIds: Array<number>;
	requiresCimsAccess: boolean;
	messageStatuses: ReadonlyArray<number>;
	fadeSkipped: boolean;
};

const SYNC_MARKETPLACES: ReadonlyArray<TSyncMarketplace> = [
	{
		prefix: "CIMS",
		liveId: ExportDestination.Cims,
		anchorId: ExportDestination.Cims,
		renderOnIds: [ExportDestination.Cims],
		requiresCimsAccess: true,
		messageStatuses: Object.values(SyncStatus).filter((id) => id !== SyncStatus.Synced),
		fadeSkipped: false,
	},
	{
		prefix: "VGG",
		liveId: ExportDestination.StubHub,
		anchorId: ExportDestination.StubHub,
		renderOnIds: [ExportDestination.StubHub, ExportDestination.ViaGoGo],
		requiresCimsAccess: false,
		messageStatuses: [SyncStatus.ErrorRetry, SyncStatus.ErrorFatal, SyncStatus.Skipped],
		fadeSkipped: true,
	},
	{
		prefix: "GT",
		liveId: ExportDestination.GameTime,
		anchorId: ExportDestination.GameTime,
		renderOnIds: [ExportDestination.GameTime],
		requiresCimsAccess: false,
		messageStatuses: [SyncStatus.ErrorRetry, SyncStatus.ErrorFatal, SyncStatus.Skipped],
		fadeSkipped: false,
	},
	{
		prefix: "TM",
		liveId: ExportDestination.TicketsNow,
		anchorId: ExportDestination.TicketsNow,
		renderOnIds: [ExportDestination.TicketsNow],
		requiresCimsAccess: false,
		messageStatuses: [SyncStatus.ErrorRetry, SyncStatus.ErrorFatal, SyncStatus.Skipped],
		fadeSkipped: true,
	},
	{
		prefix: "SG",
		liveId: ExportDestination.SeatGeek,
		anchorId: ExportDestination.SeatGeek,
		renderOnIds: [ExportDestination.SeatGeek],
		requiresCimsAccess: false,
		messageStatuses: [SyncStatus.ErrorRetry, SyncStatus.ErrorFatal, SyncStatus.Skipped],
		fadeSkipped: true,
	},
	{
		prefix: "GTX",
		liveId: ExportDestination.GoTickets,
		anchorId: ExportDestination.GoTickets,
		renderOnIds: [ExportDestination.GoTickets],
		requiresCimsAccess: false,
		messageStatuses: [SyncStatus.ErrorRetry, SyncStatus.ErrorFatal, SyncStatus.Skipped],
		fadeSkipped: true,
	},
];

type TMarketplaceSync = {
	config: TSyncMarketplace;
	status: TSyncStatus;
	message: string | null;
	notListed: boolean;
};

const unitWidth = (unit: TMarketplaceUnit): number => {
	const count = unit.destinations.length;
	return count * ICON_BOX + Math.max(0, count - 1) * MERGE_GAP;
};

const computeVisibleUnits = (containerWidth: number, widths: Array<number>): number => {
	const count = widths.length;
	if (containerWidth <= 0 || count === 0) return 0;

	const totalWidth = widths.reduce((sum, w) => sum + w, 0) + Math.max(0, count - 1) * ICON_GAP;
	if (totalWidth <= containerWidth) return count;

	const usable = containerWidth - BADGE_WIDTH;
	let used = 0;
	let fit = 0;
	for (const width of widths) {
		const add = (fit === 0 ? 0 : ICON_GAP) + width;
		if (used + add > usable) break;
		used += add;
		fit += 1;
	}
	return fit;
};

export const Marketplaces: React.FC<TMarketplaces> = ({ rowData }) => {
	const { data: destinations, loading, init } = useAppSelector((state) => state.system.exportDestinations);
	const hasCIMSMarketAccess = useAppSelector((state) => state.settings.hasCIMSMarketAccess);
	const hasStageFrontRole = useAppSelector((state) => state.settings.hasStageFrontRole);
	const isGametimeSyncEnabled = useGametimeListingStatusEnabled() && hasStageFrontRole;
	const gametimeListings = useAppSelector((state) => state.marketplace.gameTime.activeListings.data);

	const containerRef = useRef<HTMLDivElement>(null);
	const [maxVisible, setMaxVisible] = useState<number | null>(null);

	const activeIds = getActiveMarketplaceIds(rowData);
	const idToDestination = new Map(destinations.map((d) => [d.Export_Destination_ID, d]));

	const syncs = SYNC_MARKETPLACES.flatMap((config): Array<TMarketplaceSync> => {
		if (config.requiresCimsAccess && !hasCIMSMarketAccess) return [];
		if (config.prefix === "GT" && !isGametimeSyncEnabled) return [];
		const status = getSyncStatus(rowData[`${config.prefix}_Sync_Status`]);
		if (!status) return [];
		const isLive = activeIds.includes(config.liveId);
		if (!isLive && status.id === SyncStatus.Synced) return [];
		const message = config.messageStatuses.includes(status.id)
			? (rowData[`${config.prefix}_Sync_Message`] ?? "").trim() || null
			: null;
		return [{ config, status, message, notListed: !isLive }];
	});
	const pendingAnchorIds = syncs
		.filter(
			(s) =>
				s.notListed && !activeIds.some((id) => s.config.renderOnIds.includes(id)) && idToDestination.has(s.config.anchorId),
		)
		.map((s) => s.config.anchorId);
	const visibleIds = [...pendingAnchorIds, ...activeIds];

	const matched = visibleIds.map((id) => idToDestination.get(id)).filter((d): d is NonNullable<typeof d> => !!d);
	const units = buildMarketplaceUnits(matched, destinations);
	const unitWidths = units.map(unitWidth);
	const widthsKey = unitWidths.join("-");

	const globalRow = rowData as IGlobalInventoryRawItem;
	const isOnHold =
		rowData.On_Hold || rowData.Consignor_Hold || (globalRow.Num_OnHold ?? 0) > 0 || (globalRow.Num_Consignor_Hold ?? 0) > 0;

	useLayoutEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const compute = () => {
			setMaxVisible(computeVisibleUnits(el.clientWidth, unitWidths));
		};
		compute();
		const ro = new ResizeObserver(compute);
		ro.observe(el);
		return () => ro.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [widthsKey]);

	useEffect(() => {
		setMaxVisible((prev) => (prev == null ? prev : Math.min(prev, units.length)));
	}, [units.length]);

	if (isOnHold) return null;

	if (!init || (loading && !destinations.length)) {
		return (
			<div ref={containerRef} className={styles.marketplaceContainer} style={{ width: "100%" }}>
				<div className={styles.marketplaceIcons}>
					<Loader controlSize="sm" />
				</div>
			</div>
		);
	}

	const syncClassName = (sync: TMarketplaceSync): string =>
		[
			styles.marketplaceSync,
			styles[sync.status.className],
			sync.notListed ? styles.syncNotListed : "",
			sync.config.fadeSkipped && sync.status.id === SyncStatus.Skipped ? styles.syncSkippedFaded : "",
		]
			.filter(Boolean)
			.join(" ");

	const renderIcon = (d: NonNullable<ReturnType<typeof idToDestination.get>>) => {
		const sync = syncs.find((s) => s.config.anchorId === d.Export_Destination_ID);
		return (
			<MarketplaceIcon
				destination={d}
				alt={sync ? `${d.Exchange_Name} - ${sync.status.label}${sync.message ? ` - ${sync.message}` : ""}` : d.Exchange_Name}
				className={[styles.marketplaceIcon, sync ? syncClassName(sync) : ""].filter(Boolean).join(" ")}
				style={{ width: ICON_BOX, height: ICON_BOX, objectFit: "contain", flexShrink: 0 }}
			/>
		);
	};

	// Only Gametime's own listing status says whether a listing is live there; the inventory row carries just our sync state.
	const gametimeListing =
		gametimeListings && gametimeListings.eventId === rowData.Event_ID
			? gametimeListings.listings.find((l) => l.listingId === rowData.Listing_ID)
			: undefined;
	const showsCurationHint = (unit: TMarketplaceUnit): boolean =>
		unit.ids.includes(ExportDestination.GameTime) &&
		activeIds.includes(ExportDestination.GameTime) &&
		!!gametimeListing &&
		showsGametimeCurationHint({ ...gametimeListing, syncStatus: gametimeListing.syncStatus ?? rowData.GT_Sync_Status });

	const unitLabel = (unit: TMarketplaceUnit): string => {
		const label = syncs
			.filter((s) => unit.ids.some((id) => s.config.renderOnIds.includes(id)))
			.reduce(
				(acc, s) =>
					`${acc} — ${s.status.label}${s.message ? ` — ${s.message}` : ""}${s.notListed ? " — not currently listed" : ""}`,
				unit.label,
			);
		return showsCurationHint(unit) ? `${label} — ${GAMETIME_CURATION_DETAIL}` : label;
	};

	if (maxVisible == null) {
		return <div ref={containerRef} className={styles.marketplaceContainer} style={{ width: "100%" }} />;
	}

	const visibleCount = Math.max(0, Math.min(maxVisible, units.length));
	const visible = units.slice(0, visibleCount);
	const hidden = units.slice(visibleCount);

	const visibleIcons = visible.map((unit) => (
		<MyTooltip
			key={unit.key}
			id={`mp-icons-${rowData.Listing_ID}-${unit.key}`}
			popup={unitLabel(unit)}
			element={
				<span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: MERGE_GAP }}>
					{unit.destinations.map((d) => (
						<span key={d.Export_Destination_ID} style={{ display: "inline-flex", alignItems: "center" }}>
							{renderIcon(d)}
						</span>
					))}
				</span>
			}
		/>
	));

	const overflowBadge =
		hidden.length > 0 ? (
			<MyTooltip
				key="mp-overflow"
				id={`mp-icons-overflow-${rowData.Listing_ID}`}
				popup={
					<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
						{hidden.map((unit) => (
							<span key={unit.key} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
								<span style={{ display: "inline-flex", alignItems: "center", gap: MERGE_GAP }}>
									{unit.destinations.map((d) => (
										<span key={d.Export_Destination_ID} style={{ display: "inline-flex", alignItems: "center" }}>
											{renderIcon(d)}
										</span>
									))}
								</span>
								<span style={{ fontSize: "1.1rem" }}>{unitLabel(unit)}</span>
							</span>
						))}
					</div>
				}
				element={
					<span
						style={{
							flexShrink: 0,
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							padding: "0 6px",
							height: ICON_BOX,
							minWidth: 22,
							borderRadius: 10,
							fontSize: "1.1rem",
							fontWeight: 600,
							background: "var(--textDetails, #888)",
							color: "#fff",
							lineHeight: 1,
						}}>
						+{hidden.length}
					</span>
				}
			/>
		) : null;

	return (
		<div ref={containerRef} className={styles.marketplaceContainer} style={{ width: "100%" }}>
			<div className={styles.marketplaceIcons}>
				{visibleIcons}
				{overflowBadge}
			</div>
		</div>
	);
};
